import React from "react";
import { TransitionGroup } from "react-transition-group";

import { Store } from "@apps/viewer/src/ts/store";
import { ElementTypes, ReferenceBlockModel } from "@courses-models/course";
import { ContentModel, IframeModel, IframeTargets } from "@courses-pm2/api";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { config } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/config";
import { colors } from "@apps/viewer/src/ts/config";

import {
    Preview,
    PreviewProps,
    PreviewState,
    PreviewTitle,
} from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/Preview";
import {
    ReferencePreviewFade,
} from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/transitions/ReferencePreviewFade";
import { urls } from "@courses-pm2/api/src/ts/config";

type ReferencePreviewProps = {
    /**
     * Contains a reference to the application lifecycle store, so that we can
     * reference data about the currently signed in user.
     */
    store: Store;
};

type ReferencePreviewState = {
    /**
     * Will contain the content model describing the actual content of the
     * referenced element once it has been fetched.
     */
    content?: ContentModel;
};

/**
 * Renders a preview of Youtube blocks consisting of title, thumbnail and video
 * duration (once fetched).
 */
export class ReferencePreview extends Preview<
    PreviewProps<ReferenceBlockModel> & ReferencePreviewProps,
    ReferencePreviewState & PreviewState
    > {
    /**
     * Set up initial state.
     */
    public readonly state: ReferencePreviewState & PreviewState = {
        isActive: false,
    };

    /**
     * Return the fallback icon/text based on whether content is loading and if
     * the user is authorized or not.
     */
    protected renderThumbnailFallbackIcon(): React.JSX.Element {
        // Disable no-null-keyword since we have to use 'null' in order to render
        // no DOM element for the transition group, undefined won't cut it here.
        // tslint:disable no-null-keyword
        return (
            <React.Fragment>
                <TransitionGroup component={null}>
                    {!this.state.content && (
                        <ReferencePreviewFade key="throbber">
                            <i className="throbber" />
                        </ReferencePreviewFade>
                    )}

                    {this.state.content && !this.state.content.isAuthorized && (
                        <ReferencePreviewFade key="unauthorized">
                            {this.renderUnAuthorizedFallbackPreview()}
                        </ReferencePreviewFade>
                    )}

                    {this.state.content && this.state.content.isAuthorized && (
                        <ReferencePreviewFade key="authorized">
                            {this.renderAuthorizedFallbackPreview()}
                        </ReferencePreviewFade>
                    )}
                </TransitionGroup>

                <style jsx>{`
                    .throbber {
                        /** Center the throbber */
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        margin-left: -24px;
                        margin-top: -24px;

                        /** Set up throbber sizing */
                        box-sizing: border-box;
                        width: 48px;
                        height: 48px;

                        /** Apply throbber styling */
                        border: 3px solid rgba(220, 67, 32, 0.25);
                        border-left-color: ${ colors.orange};
                        border-radius: 50%;

                        /** Force rendering of pseudo element */
                        content: "";

                        /** Spin around infinitely */
                        animation: spin 0.9s linear infinite;
                    }

                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </React.Fragment>
        );
        // tslint:enable no-null-keyword
    }

    /**
     * Renderer that'll inject the fallback icon, which is going to be used if
     * no thumbnail could be found for this block.
     */
    protected renderAuthorizedFallbackPreview(): React.JSX.Element {
        let icon: React.JSX.Element;

        // ... Otherwise render icon based on item type
        switch (this.props.model.element.type) {
            case ElementTypes.AUDIO:
                icon = getIcon("audio", { fillColor: config.thumbnailIconColor });
                break;

            case ElementTypes.COMPLEXT_CONTENT:
                icon = getIcon("richtext", { fillColor: config.thumbnailIconColor });
                break;

            case ElementTypes.IFRAME:
                icon = getIcon("component", { fillColor: config.thumbnailIconColor });
                break;

            case ElementTypes.IMAGE:
                icon = getIcon("image", { fillColor: config.thumbnailIconColor });
                break;

            case ElementTypes.RICHTEXT:
                icon = getIcon("text2", { fillColor: config.thumbnailIconColor });
                break;

            case ElementTypes.VIDEO:
                icon = getIcon("video", { strokeColor: config.thumbnailIconColor });
                break;

            default:
                // Fall back to page icon
                icon = getIcon("page", { fillColor: config.thumbnailIconColor });
        }

        return (
            <div>
                <i>{icon}</i>

                <div className="text-wrapper">
                    <span className="snippet">
                        {this.state.content && this.state.content.getPreviewText()}
                    </span>
                </div>

                <style jsx>{`
                    div {
                        /**
                         * Fill container in order to ensure gradient positions
                         * itself properly
                         */
                        height: 100%;
                        width: 100%;
                        box-sizing: border-box;
                    }

                    /** Position the icon */
                    i {
                        /** Position at the bottom-left corner */
                        position: absolute;
                        top: 36px;
                        right: -58px;

                        /** Set up sizing */
                        width: 200px;
                        height: 150px;

                        /** Position text above icon */
                        z-index: 1;

                        &::after {
                            /** Overlap the container */
                            position: absolute;
                            left: 0;
                            bottom: 0;

                            /** Set up sizing of the backdrop */
                            height: 100%;
                            width: 100%;

                            /** Ensure graident is rendered below text */
                            z-index: 1;

                            /** Fade out using a gradient */
                            background: linear-gradient(to bottom, #f3f3f399, #f3f3f300);

                            /** Force rendering of pseudo element */
                            content: "";
                        }
                    }

                    .text-wrapper {
                        /** Setup internal sizing */
                        padding: 15px 25px;

                        /** Ensure text is above icon */
                        position: relative;
                        z-index: 2;
                    }

                    .snippet {
                        /** Allow up to 5 lines before cropping text */
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: 5;
                        overflow: hidden;

                        /**
                            * On non-webkit browsers simply enforce max-height
                            * to crop to 5 lines.
                            */
                        max-height: calc(5em * 1.5 - 0.25em);

                        /** Since we use -webkit-box, then the white-space
                            * on top of the first line of text will remain, however
                            * there will be no whitespace on the bottom of the text block.
                            * Therefore we apply this manually to achieve equal
                            * borders.
                            */
                        padding-bottom: 0.25em;

                        /** Override defaults */
                        margin: 0;

                        /** Setup font */
                        color: ${ colors.darkGrey};
                        line-height: 1.5;
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Helper that renders the background icon behind the rest of the content.
     */
    protected renderUnAuthorizedFallbackPreview(): React.JSX.Element {
        return (
            <div className="unauthorized">
                <i className="background">
                    {getIcon("lock", { strokeColor: config.thumbnailIconColor })}
                </i>

                <span className="msg">
                    Du har ikke adgang<br />til dette indhold!
                </span>

                <style jsx>{`
                    .msg {
                        /** Position the icon */
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);

                        /** Apply text styling */
                        text-align: center;
                        line-height: 1.3em;
                    }

                    .background {
                        /** Position the icon */
                        position: absolute;
                        top: 20px;
                        right: -50px;

                        /** Set up sizing */
                        width: 180px;
                        height: 180px;
                    }
                `}</style>
            </div>
        );
    }

    /**
     * For References, both a title of the reference and a subTitle in form of
     * the prodcut title is available
     */
    protected getTitle(): PreviewTitle {
        return {
            title: this.props.model.title,
            subTitle: this.props.model.product.title,
        };
    }

    /**
     * For YouTube we can safely use the id of the embedded video as the key,
     * which our preview depends upon
     */
    protected getDataKey(): string {
        return `${this.props.model.element.id}-${!!this.state.content}`;
    }

    /**
     * Helper that'll return a url to the thumbnail image for the content that
     * this reference is pointing to.
     */
    protected async getThumbnailUrl(): Promise<string | undefined> {
        if (!this.state.content || !this.state.content.isAuthorized) {
            return;
        }

        return this.state.content.getPreviewImageUrl();
    }

    /**
     * Helper that'll fetch the content of the reference as soon as it's being
     * mounted into the DOM.
     */
    protected async fetchContent(): Promise<void> {
        const { model, store } = this.props;

        // Determine if we're fetching a page or a specific element?
        let content: ContentModel;

        switch (model.element.type) {
            case ElementTypes.PAGE:
                content = await store.engine.getPage({
                    pageId: model.element.id,
                    portalId: model.product.id,
                });
                break;

            case ElementTypes.IBOOK:
                content = new ContentModel({
                    engine: store.engine,
                    isAuthorized: true,
                    isScrambled: false,
                    elements: [
                        new IframeModel({
                            isScrambled: false,

                            engine: store.engine,
                            portalId: model.product.id,

                            aspectRatio: {
                                width: 16,
                                height: 9,
                            },
                            minSize: {
                                width: 0,
                                height: 0,
                            },
                            maxSize: undefined,

                            previewImage: undefined,
                            needsAutoGeneratedPreviewImage: true,

                            autoActivate: true,
                            inPageEmbed: true,
                            target: IframeTargets.WINDOW,
                            src: `${urls.ibook4}/${model.element.id}/`,
                        }),
                    ],
                });
                break;

            default:
                content = await store.engine.getElement({
                    elementId: model.element.id,
                    portalId: model.product.id,
                });
        }

        // Now that the content element has been fetched, update state so we
        // can properly render the preview for it
        this.setState({ content });
    }
}
